import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, X, Check, UserPlus, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { teacherGroupsAPI } from "../api";
import toast from "react-hot-toast";
import { BRAND, BRAND_BG, BRAND_BORDER } from "../theme/colors";

// ─── Types ───────────────────────────────────────────────────────
interface Group {
  id: string;
  name: string;
  max_students: number;
  created_at: string;
  members: {
    id: string;
    student: { id: string; name: string; unique_code: string };
  }[];
}

interface Invitation {
  id: string;
  status: string;
  created_at: string;
  group: {
    id: string;
    name: string;
    teacher: {
      name: string;
      phone_number: string | null;
      telegram_username: string | null;
    };
  };
}

// ─── TeacherPanelPage ─────────────────────────────────────────────
const TeacherPanelPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isTeacher = user?.role === "teacher";

  // Teacher states
  const [groups, setGroups] = useState<Group[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Student states
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [studentGroups, setStudentGroups] = useState<any[]>([]);

  // Progress overlay
  const [selectedStudent, setSelectedStudent] = useState<{
    id: string;
    name: string;
    groupId: string;
  } | null>(null);

  useEffect(() => {
    if (isTeacher) {
      loadGroups();
    } else {
      loadInvitations();
      loadStudentGroups();
    }
  }, [isTeacher]);

  const loadGroups = async () => {
    setLoadingGroups(true);
    try {
      const res = await teacherGroupsAPI.getMyGroups();
      setGroups(res.data);
    } catch (e) {
      toast.error("Guruhlarni yuklashda xato");
    } finally {
      setLoadingGroups(false);
    }
  };

  const loadInvitations = async () => {
    try {
      const res = await teacherGroupsAPI.getMyInvitations();
      setInvitations(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadStudentGroups = async () => {
    try {
      const res = await teacherGroupsAPI.getStudentGroups();
      setStudentGroups(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleRespond = async (id: string, accept: boolean) => {
    try {
      await teacherGroupsAPI.respondInvitation(id, accept);
      toast.success(accept ? "Guruhga qo'shildingiz!" : "Taklif rad etildi");
      loadInvitations();
      if (accept) loadStudentGroups();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Xato");
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      {/* HEADER */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: "rgba(10,14,26,.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--border)",
          height: 52,
        }}>
        <div
          style={{
            maxWidth: 520,
            margin: "0 auto",
            padding: "0 16px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={() => navigate("/dashboard")}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--muted)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                fontWeight: 600,
              }}>
              <ArrowLeft size={16} /> Orqaga
            </button>
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
              {isTeacher ? "Guruhlarim" : "Mening guruhlarim"}
            </span>
          </div>

          {/* Ustoz uchun guruh yaratish tugmasi */}
          {isTeacher && (
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 14px",
                borderRadius: 10,
                border: "none",
                background: "#3B82F6",
                color: "#fff",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
              }}>
              <Plus size={14} /> Guruh
            </button>
          )}
        </div>
      </div>

      {/* CONTENT */}
      <div
        style={{ maxWidth: 520, margin: "0 auto", padding: "68px 16px 32px" }}>
        {/* ── USTOZ VIEW ── */}
        {isTeacher && (
          <>
            {loadingGroups ?
              <div
                style={{
                  textAlign: "center",
                  padding: 40,
                  color: "var(--muted)",
                }}>
                Yuklanmoqda...
              </div>
            : groups.length === 0 ?
              <div
                style={{
                  textAlign: "center",
                  padding: 48,
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  borderRadius: 14,
                }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>👨‍🏫</div>
                <p
                  style={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 16,
                    marginBottom: 6,
                  }}>
                  Hali guruh yo'q
                </p>
                <p
                  style={{
                    color: "var(--muted)",
                    fontSize: 13,
                    marginBottom: 20,
                  }}>
                  Birinchi guruhingizni yarating
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  style={{
                    padding: "10px 24px",
                    borderRadius: 10,
                    border: "none",
                    background: "#3B82F6",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: "pointer",
                  }}>
                  + Guruh yaratish
                </button>
              </div>
            : <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {groups.map((group) => (
                  <div
                    key={group.id}
                    style={{
                      background: "var(--card)",
                      border: "1px solid var(--border)",
                      borderRadius: 14,
                      padding: 16,
                    }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 12,
                      }}>
                      <div>
                        <p
                          style={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 15,
                          }}>
                          {group.name}
                        </p>
                        <p
                          style={{
                            color: "var(--muted)",
                            fontSize: 12,
                            marginTop: 2,
                          }}>
                          {group.members?.length || 0} / {group.max_students}{" "}
                          o'quvchi
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedGroup(group);
                          setShowInviteModal(true);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "7px 12px",
                          borderRadius: 10,
                          border: "1px solid var(--border)",
                          background: "rgba(59,130,246,.1)",
                          color: "#3B82F6",
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: "pointer",
                        }}>
                        <UserPlus size={13} /> Qo'shish
                      </button>
                    </div>

                    {/* O'quvchilar ro'yxati */}
                    {group.members?.length > 0 ?
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}>
                        {group.members.map((m) => (
                          <div
                            key={m.id}
                            onClick={() =>
                              setSelectedStudent({
                                id: m.student.id,
                                name: m.student.name,
                                groupId: group.id,
                              })
                            }
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "8px 12px",
                              background: "#0D1526",
                              borderRadius: 10,
                              border: "1px solid var(--border)",
                              cursor: "pointer",
                            }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                              }}>
                              <div
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  background: BRAND_BG,
                                  border: `1px solid ${BRAND_BORDER}`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: BRAND,
                                  fontWeight: 800,
                                  fontSize: 13,
                                }}>
                                {m.student.name?.charAt(0)?.toUpperCase()}
                              </div>
                              <div>
                                <span
                                  style={{
                                    color: "#fff",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    display: "block",
                                  }}>
                                  {m.student.name}
                                </span>
                                <span
                                  style={{
                                    color: "var(--muted)",
                                    fontSize: 10,
                                    fontFamily: "monospace",
                                  }}>
                                  {m.student.unique_code}
                                </span>
                              </div>
                            </div>
                            <ChevronRight
                              size={14}
                              style={{ color: "var(--muted)", flexShrink: 0 }}
                            />
                          </div>
                        ))}
                      </div>
                    : <p
                        style={{
                          color: "var(--muted)",
                          fontSize: 12,
                          textAlign: "center",
                          padding: "8px 0",
                        }}>
                        Hali o'quvchi yo'q
                      </p>
                    }
                  </div>
                ))}
              </div>
            }
          </>
        )}

        {/* ── O'QUVCHI VIEW ── */}
        {!isTeacher && (
          <>
            {/* Pending takliflar */}
            {invitations.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <p
                  style={{
                    color: "var(--muted)",
                    fontSize: 12,
                    fontWeight: 700,
                    marginBottom: 10,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}>
                  Kutilayotgan takliflar
                </p>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {invitations.map((inv) => (
                    <div
                      key={inv.id}
                      style={{
                        background: "var(--card)",
                        border: "1px solid #3B82F6",
                        borderRadius: 14,
                        padding: 16,
                      }}>
                      <p
                        style={{
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 14,
                          marginBottom: 4,
                        }}>
                        {inv.group.name}
                      </p>
                      <p
                        style={{
                          color: "var(--muted)",
                          fontSize: 12,
                          marginBottom: 14,
                        }}>
                        Ustoz: {inv.group.teacher.name}
                      </p>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={() => handleRespond(inv.id, true)}
                          style={{
                            flex: 1,
                            padding: "9px",
                            borderRadius: 10,
                            border: "none",
                            background: "#22C55E",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 13,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                          }}>
                          <Check size={14} /> Qabul
                        </button>
                        <button
                          onClick={() => handleRespond(inv.id, false)}
                          style={{
                            flex: 1,
                            padding: "9px",
                            borderRadius: 10,
                            border: "1px solid var(--border)",
                            background: "transparent",
                            color: "var(--muted)",
                            fontWeight: 700,
                            fontSize: 13,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                          }}>
                          <X size={14} /> Rad
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Guruhlar */}
            <p
              style={{
                color: "var(--muted)",
                fontSize: 12,
                fontWeight: 700,
                marginBottom: 10,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}>
              Mening guruhlarim
            </p>
            {studentGroups.length === 0 ?
              <div
                style={{
                  textAlign: "center",
                  padding: 48,
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  borderRadius: 14,
                }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🎓</div>
                <p
                  style={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 16,
                    marginBottom: 6,
                  }}>
                  Hali guruhga qo'shilmagan
                </p>
                <p style={{ color: "var(--muted)", fontSize: 13 }}>
                  Ustoz taklif yuborganida bu yerda ko'rinadi
                </p>
              </div>
            : <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {studentGroups.map((sg) => (
                  <div
                    key={sg.group_id}
                    style={{
                      background: "var(--card)",
                      border: "1px solid var(--border)",
                      borderRadius: 14,
                      padding: 16,
                    }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 12,
                        paddingBottom: 12,
                        borderBottom: "1px solid var(--border)",
                      }}>
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          background: "rgba(59,130,246,.15)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 20,
                        }}>
                        👨‍🏫
                      </div>
                      <div>
                        <p
                          style={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 14,
                          }}>
                          {sg.group_name}
                        </p>
                        <p style={{ color: "var(--muted)", fontSize: 12 }}>
                          {sg.members_count} o'quvchi
                        </p>
                      </div>
                    </div>

                    {/* Ustoz ma'lumotlari */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}>
                        <span style={{ color: "var(--muted)", fontSize: 12 }}>
                          Ustoz:
                        </span>
                        <span
                          style={{
                            color: "#fff",
                            fontSize: 13,
                            fontWeight: 600,
                          }}>
                          {sg.teacher.name}
                        </span>
                      </div>
                      {sg.teacher.phone_number && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}>
                          <span style={{ color: "var(--muted)", fontSize: 12 }}>
                            📞
                          </span>
                          <span style={{ color: "#60A5FA", fontSize: 13 }}>
                            {sg.teacher.phone_number}
                          </span>
                        </div>
                      )}
                      {sg.teacher.telegram_username && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}>
                          <span style={{ color: "var(--muted)", fontSize: 12 }}>
                            ✈️
                          </span>
                          <span style={{ color: "#60A5FA", fontSize: 13 }}>
                            @{sg.teacher.telegram_username}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            }
          </>
        )}
      </div>

      {/* ── GURUH YARATISH MODAL ── */}
      {showCreateModal && (
        <CreateGroupModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            loadGroups();
          }}
        />
      )}

      {/* ── TAKLIF YUBORISH MODAL ── */}
      {showInviteModal && selectedGroup && (
        <InviteModal
          group={selectedGroup}
          onClose={() => {
            setShowInviteModal(false);
            setSelectedGroup(null);
          }}
          onInvited={() => {
            setShowInviteModal(false);
            loadGroups();
          }}
        />
      )}

      {/* ── O'QUVCHI PROGRESS OVERLAY ── */}
      {selectedStudent && (
        <StudentProgressOverlay
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
        />
      )}
    </div>
  );
};

// ─── Create Group Modal ───────────────────────────────────────────
const CreateGroupModal: React.FC<{
  onClose: () => void;
  onCreated: () => void;
}> = ({ onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [maxStudents, setMaxStudents] = useState(30);
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Guruh nomini kiriting");
      return;
    }
    setLoading(true);
    try {
      await teacherGroupsAPI.createGroup({
        name: name.trim(),
        max_students: maxStudents,
      });
      toast.success("Guruh yaratildi!");
      onCreated();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Xato");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
      onClick={onClose}>
      <div
        style={{
          background: "#111827",
          border: "1px solid var(--border)",
          borderRadius: 16,
          padding: 24,
          width: "100%",
          maxWidth: 340,
        }}
        onClick={(e) => e.stopPropagation()}>
        <h3
          style={{
            color: "#fff",
            fontWeight: 800,
            fontSize: 16,
            marginBottom: 20,
          }}>
          Guruh yaratish
        </h3>

        <div style={{ marginBottom: 14 }}>
          <label
            style={{
              display: "block",
              color: "var(--muted)",
              fontSize: 11,
              fontWeight: 700,
              marginBottom: 6,
            }}>
            Guruh nomi
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Masalan: 1-guruh"
            style={{
              width: "100%",
              background: "#0D1526",
              border: "1px solid var(--border)",
              borderRadius: 10,
              padding: "10px 14px",
              color: "#E2E8F0",
              fontSize: 14,
              outline: "none",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3B82F6")}
            onBlur={(e) => (e.target.style.borderColor = "var(--border)")}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label
            style={{
              display: "block",
              color: "var(--muted)",
              fontSize: 11,
              fontWeight: 700,
              marginBottom: 6,
            }}>
            Maksimal o'quvchi soni: {maxStudents}
          </label>
          <input
            type="range"
            min={5}
            max={100}
            value={maxStudents}
            onChange={(e) => setMaxStudents(Number(e.target.value))}
            style={{ width: "100%", accentColor: "#3B82F6" }}
          />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "var(--muted)", fontSize: 11 }}>5</span>
            <span style={{ color: "var(--muted)", fontSize: 11 }}>100</span>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: "11px",
              borderRadius: 10,
              border: "1px solid var(--border)",
              background: "transparent",
              color: "var(--muted)",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
            }}>
            Bekor
          </button>
          <button
            onClick={handleCreate}
            disabled={loading}
            style={{
              flex: 1,
              padding: "11px",
              borderRadius: 10,
              border: "none",
              background: "#3B82F6",
              color: "#fff",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
              opacity: loading ? 0.8 : 1,
            }}>
            {loading ? "..." : "Yaratish"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Invite Modal ─────────────────────────────────────────────────
const InviteModal: React.FC<{
  group: Group;
  onClose: () => void;
  onInvited: () => void;
}> = ({ group, onClose, onInvited }) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleInvite = async () => {
    if (code.replace(/-/g, "").length !== 9) {
      toast.error("9 raqamli kod kiriting");
      return;
    }
    setLoading(true);
    try {
      const cleanCode = code.replace(/-/g, "");
      const res = await teacherGroupsAPI.inviteStudent(group.id, cleanCode);
      toast.success(`${res.data.student_name} ga taklif yuborildi!`);
      onInvited();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Xato");
    } finally {
      setLoading(false);
    }
  };

  // Auto format: 315-942-308
  const handleCodeChange = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 9);
    let formatted = digits;
    if (digits.length > 6)
      formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    else if (digits.length > 3)
      formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    setCode(formatted);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
      onClick={onClose}>
      <div
        style={{
          background: "#111827",
          border: "1px solid var(--border)",
          borderRadius: 16,
          padding: 24,
          width: "100%",
          maxWidth: 340,
        }}
        onClick={(e) => e.stopPropagation()}>
        <h3
          style={{
            color: "#fff",
            fontWeight: 800,
            fontSize: 16,
            marginBottom: 6,
          }}>
          O'quvchi qo'shish
        </h3>
        <p style={{ color: "var(--muted)", fontSize: 12, marginBottom: 20 }}>
          Guruh: <span style={{ color: "#60A5FA" }}>{group.name}</span>
        </p>

        <div style={{ marginBottom: 20 }}>
          <label
            style={{
              display: "block",
              color: "var(--muted)",
              fontSize: 11,
              fontWeight: 700,
              marginBottom: 6,
            }}>
            O'quvchining 9 raqamli kodi
          </label>
          <input
            value={code}
            onChange={(e) => handleCodeChange(e.target.value)}
            placeholder="315-942-308"
            style={{
              width: "100%",
              background: "#0D1526",
              border: "1px solid var(--border)",
              borderRadius: 10,
              padding: "12px 14px",
              color: "#60A5FA",
              fontSize: 18,
              fontWeight: 800,
              fontFamily: "monospace",
              letterSpacing: 3,
              outline: "none",
              textAlign: "center",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3B82F6")}
            onBlur={(e) => (e.target.style.borderColor = "var(--border)")}
          />
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: "11px",
              borderRadius: 10,
              border: "1px solid var(--border)",
              background: "transparent",
              color: "var(--muted)",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
            }}>
            Bekor
          </button>
          <button
            onClick={handleInvite}
            disabled={loading}
            style={{
              flex: 1,
              padding: "11px",
              borderRadius: 10,
              border: "none",
              background: "#3B82F6",
              color: "#fff",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
              opacity: loading ? 0.8 : 1,
            }}>
            {loading ? "..." : "Taklif yuborish"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────
const getSessionLabel = (session: any): string => {
  if (session.mode === "exam") return "Nazorat imtihoni";
  if (session.mode === "topic")
    return `Mavzu: ${session.topic?.name_uz || ""}`;
  if (session.mode === "ticket")
    return `Bilet #${session.ticket?.bob ?? ""}`;
  return "Aralash test";
};

const relativeTime = (dateStr: string): string => {
  const created = new Date(dateStr);
  const today = new Date();
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const createdMidnight = new Date(created.getFullYear(), created.getMonth(), created.getDate()).getTime();
  const days = Math.round((todayMidnight - createdMidnight) / 86_400_000);
  if (days === 0) return "Bugun";
  if (days === 1) return "Kecha";
  return `${days} kun oldin`;
};

// ─── Student Progress Overlay ─────────────────────────────────────
const StudentProgressOverlay: React.FC<{
  student: { id: string; name: string; groupId: string };
  onClose: () => void;
}> = ({ student, onClose }) => {
  const [progress, setProgress] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    teacherGroupsAPI
      .getStudentProgress(student.groupId, student.id, 20)
      .then((res) => setProgress(res.data))
      .catch(() => toast.error("Progress yuklanmadi"))
      .finally(() => setLoading(false));
  }, [student.groupId, student.id]);

  const { readiness, ticketsDone, topicsDone } = useMemo(() => {
    if (!progress) return { readiness: 0, ticketsDone: 0, topicsDone: 0 };

    const tp: any[] = progress.ticketProgress || [];
    const top: any[] = progress.topicProgress || [];
    const tDone = tp.filter((x) => Number(x?.bestScore || 0) >= 85).length;
    const topDone = top.filter((x) => Number(x?.bestScore || 0) >= 85).length;

    const ticketPct = (tDone / 61) * 40;
    const topicPct = (topDone / 42) * 40;
    const totalWrong = progress.summary?.totalWrong || 0;
    const somethingDone = (progress.summary?.totalSessions || 0) > 0;
    const mistakePct =
      somethingDone ?
        totalWrong === 0 ? 20
        : Math.max(0, 20 - Math.floor(totalWrong / 5))
      : 0;

    return {
      readiness: Math.min(100, Math.round(ticketPct + topicPct + mistakePct)),
      ticketsDone: tDone,
      topicsDone: topDone,
    };
  }, [progress]);

  const cardBase: React.CSSProperties = {
    background: "var(--card)",
    border: "1px solid var(--border)",
    borderRadius: 14,
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "var(--bg)",
        display: "flex",
        flexDirection: "column",
      }}>
      {/* Header */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 201,
          background: "rgba(10,14,26,.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--border)",
          height: 52,
        }}>
        <div
          style={{
            maxWidth: 520,
            margin: "0 auto",
            padding: "0 16px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--muted)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
            }}>
            <ArrowLeft size={16} /> Orqaga
          </button>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
            {student.name}
          </span>
        </div>
      </div>

      {/* Content */}
      <div
        style={{
          maxWidth: 520,
          margin: "0 auto",
          padding: "68px 16px 32px",
          width: "100%",
          overflowY: "auto",
          flex: 1,
        }}>
        {loading ?
          <div
            style={{ textAlign: "center", padding: 48, color: "var(--muted)" }}>
            Yuklanmoqda...
          </div>
        : !progress ?
          <div
            style={{ textAlign: "center", padding: 48, color: "var(--muted)" }}>
            Ma'lumot yuklanmadi
          </div>
        : <>
            {/* Student info */}
            <div
              style={{
                ...cardBase,
                padding: "14px 16px",
                marginBottom: 10,
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: BRAND_BG,
                  border: `1px solid ${BRAND_BORDER}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: BRAND,
                  fontWeight: 800,
                  fontSize: 18,
                  flexShrink: 0,
                }}>
                {progress.student?.telegram_photo_url ?
                  <img
                    src={progress.student.telegram_photo_url}
                    alt=""
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                : student.name?.charAt(0)?.toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p
                  style={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 15,
                    marginBottom: 2,
                  }}>
                  {student.name}
                </p>
                {progress.student?.telegram_username && (
                  <p style={{ color: "var(--muted)", fontSize: 12 }}>
                    @{progress.student.telegram_username}
                  </p>
                )}
                {progress.student?.phone_number && (
                  <p style={{ color: "var(--muted)", fontSize: 12 }}>
                    {progress.student.phone_number}
                  </p>
                )}
              </div>
              <span
                style={{
                  color: "var(--muted)",
                  fontSize: 11,
                  fontFamily: "monospace",
                  flexShrink: 0,
                }}>
                {progress.student?.unique_code}
              </span>
            </div>

            {/* Readiness bar */}
            <div
              style={{
                ...cardBase,
                padding: "14px 16px",
                marginBottom: 10,
              }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}>
                <span
                  style={{
                    color: "var(--muted)",
                    fontWeight: 600,
                    fontSize: 12,
                  }}>
                  Tayyor bo'lish darajasi
                </span>
                <span
                  style={{ fontWeight: 800, fontSize: 13, color: BRAND }}>
                  {readiness}%
                </span>
              </div>
              <div
                style={{
                  height: 6,
                  background: "var(--border)",
                  borderRadius: 99,
                  overflow: "hidden",
                }}>
                <div
                  style={{
                    height: "100%",
                    width: `${readiness}%`,
                    background: BRAND,
                    borderRadius: 99,
                    transition: "width 1.2s ease",
                  }}
                />
              </div>
              <p style={{ color: "var(--muted)", fontSize: 11, marginTop: 6 }}>
                {readiness >= 80 ?
                  "Imtihonga tayyor ✓"
                : readiness >= 50 ?
                  "Yaxshi yo'lda, davom eting"
                : "Ko'proq mashq kerak"}
              </p>
            </div>

            {/* 3-grid stats */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 8,
                marginBottom: 10,
              }}>
              {[
                { label: "Biletlar (≥85%)", value: ticketsDone },
                { label: "Mavzular (≥85%)", value: topicsDone },
                {
                  label: "O'rtacha ball",
                  value: `${progress.summary?.avgScore ?? 0}%`,
                },
              ].map((s, i) => (
                <div
                  key={i}
                  style={{ ...cardBase, padding: "12px 10px", textAlign: "center" }}>
                  <p
                    style={{
                      color: "#fff",
                      fontSize: 22,
                      fontWeight: 800,
                      marginBottom: 4,
                    }}>
                    {s.value}
                  </p>
                  <p
                    style={{
                      color: "var(--muted)",
                      fontSize: 10,
                      fontWeight: 600,
                      lineHeight: 1.3,
                    }}>
                    {s.label}
                  </p>
                </div>
              ))}
            </div>

            {/* Extra summary */}
            <div style={{ ...cardBase, padding: "14px 16px", marginBottom: 14 }}>
              {[
                {
                  label: "Jami sessiyalar",
                  value: progress.summary?.totalSessions ?? 0,
                },
                {
                  label: "Tugatilgan sessiyalar",
                  value: progress.summary?.completedSessions ?? 0,
                },
                {
                  label: "Jami javoblar",
                  value: progress.summary?.totalAnswered ?? 0,
                },
                {
                  label: "To'g'ri javoblar",
                  value: progress.summary?.totalCorrect ?? 0,
                },
                {
                  label: "Xato javoblar",
                  value: progress.summary?.totalWrong ?? 0,
                },
              ].map((row, i, arr) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingTop: i === 0 ? 0 : 10,
                    paddingBottom: i === arr.length - 1 ? 0 : 10,
                    borderBottom:
                      i < arr.length - 1 ?
                        "1px solid var(--border)"
                      : "none",
                  }}>
                  <span style={{ color: "var(--muted)", fontSize: 13 }}>
                    {row.label}
                  </span>
                  <span
                    style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>
                    {row.value}
                  </span>
                </div>
              ))}
            </div>

            {/* So'nggi sessiyalar */}
            <p
              style={{
                color: "var(--muted)",
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 1,
                marginBottom: 8,
              }}>
              So'nggi sessiyalar
            </p>
            {!Array.isArray(progress.recentSessions) ||
            progress.recentSessions.length === 0 ?
              <div
                style={{
                  ...cardBase,
                  padding: "14px 16px",
                  color: "var(--muted)",
                  fontSize: 13,
                  textAlign: "center",
                  marginBottom: 14,
                }}>
                Hali birorta sessiya yo'q
              </div>
            : <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  marginBottom: 14,
                }}>
                {progress.recentSessions.map((s: any, i: number) => (
                  <div
                    key={i}
                    style={{
                      ...cardBase,
                      padding: "10px 14px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}>
                    <div>
                      <p style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>
                        {getSessionLabel(s)}
                      </p>
                      <p
                        style={{
                          color: "var(--muted)",
                          fontSize: 11,
                          marginTop: 2,
                        }}>
                        {relativeTime(s.createdAt)}
                      </p>
                    </div>
                    <span
                      style={{
                        color:
                          s.score >= 85 ? "#22C55E"
                          : s.score >= 60 ? "#F59E0B"
                          : "#EF4444",
                        fontWeight: 800,
                        fontSize: 14,
                        flexShrink: 0,
                      }}>
                      {s.score}%
                    </span>
                  </div>
                ))}
              </div>
            }

            {/* Muammo biletlar */}
            {(() => {
              const allProblematic = (progress.ticketProgress as any[] ?? [])
                .filter((t) => Number(t.bestScore ?? 0) < 85)
                .sort((a, b) => Number(a.bestScore) - Number(b.bestScore));
              const shown = allProblematic.slice(0, 6);
              const remaining = allProblematic.length - shown.length;
              return (
                <>
                  <p
                    style={{
                      color: "var(--muted)",
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      marginBottom: 8,
                    }}>
                    Muammo biletlar
                  </p>
                  {allProblematic.length === 0 ?
                    <div
                      style={{
                        ...cardBase,
                        padding: "14px 16px",
                        color: "#22C55E",
                        fontSize: 13,
                        textAlign: "center",
                      }}>
                      Yaxshi natija — muammo bilet yo'q 👍
                    </div>
                  : <>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {shown.map((t) => (
                          <div
                            key={t.ticketId}
                            style={{
                              background: "rgba(239,68,68,.1)",
                              border: "1px solid rgba(239,68,68,.3)",
                              borderRadius: 10,
                              padding: "6px 12px",
                            }}>
                            <span
                              style={{
                                color: "#E2E8F0",
                                fontSize: 12,
                                fontWeight: 700,
                              }}>
                              Bilet #{t.ticketId}
                            </span>
                            <span
                              style={{
                                color: "#EF4444",
                                fontSize: 11,
                                marginLeft: 6,
                              }}>
                              {t.bestScore ?? 0}%
                            </span>
                          </div>
                        ))}
                      </div>
                      {remaining > 0 && (
                        <p
                          style={{
                            color: "var(--muted)",
                            fontSize: 11,
                            textAlign: "center",
                            marginTop: 8,
                          }}>
                          +{remaining} ta yana
                        </p>
                      )}
                    </>
                  }
                </>
              );
            })()}
          </>
        }
      </div>
    </div>
  );
};

export default TeacherPanelPage;
